/**
 * The providers `r-embed` will frame, and the only thing standing between an
 * author's paste box and an iframe on a customer's page.
 *
 * This is an allowlist, not a denylist — the opposite of `is-safe-href`, and
 * deliberately so. A link navigates away; an iframe runs a third party's
 * document inside the page the widget was dropped on, with whatever
 * permissions the embedding page grants it. There is no set of dangerous
 * schemes to enumerate here, because everything that is not a provider we have
 * looked at is dangerous.
 *
 * Two consequences worth stating outright:
 *
 * **The `src` is rebuilt, never passed through.** A provider takes the URL the
 * author pasted, extracts the one or two identifiers it recognises, and
 * composes a fresh URL from a hardcoded origin. Nothing an author types
 * reaches the `src` except an id that matched a narrow pattern. A watch page,
 * a share link and an already-embeddable URL all converge on the same output.
 *
 * **The frame's privileges are the provider's, not the author's.** `sandbox`
 * and `allow` are properties of the entry below, not attributes an author can
 * set, which is also why this component has no pass-through convention. A
 * `pass-through-embed-attributes-sandbox` key would hand away every guarantee
 * on this page for the sake of consistency with `r-video`.
 */
import type { EmbedAspectRatio } from './embed-attributes';

export type EmbedProviderId = 'youtube' | 'vimeo' | 'google-maps' | 'calendly';

export type EmbedProvider = {
  id: EmbedProviderId;
  /** Provider name for the composer and for the docs. */
  label: string;
  /**
   * Hosts an author may paste, already normalised (lowercase, no `www.`).
   * Matching one of these only earns the URL a look at `toEmbedUrl`.
   */
  inputHosts: readonly string[];
  /**
   * Builds the URL that actually goes in the `src`, or returns `null` when the
   * URL is on the right host but is not something this provider can frame.
   * The origin it returns is hardcoded here, never taken from the input.
   */
  toEmbedUrl: (url: URL) => string | null;
  /** Tokens for the iframe's `sandbox`. Fixed per provider; never author-set. */
  sandbox: readonly string[];
  /** Permissions-policy tokens for the iframe's `allow`. */
  allow: readonly string[];
  allowFullScreen: boolean;
  /** The shape this provider's content wants before an author says otherwise. */
  defaultAspectRatio: EmbedAspectRatio;
  /** The iframe's accessible name when the author has not written one. */
  defaultTitle: string;
};

/**
 * `allow-scripts` with `allow-same-origin` looks like it cancels the sandbox,
 * and for a same-origin frame it would. Here the framed document is
 * cross-origin, so "same origin" means youtube.com's own origin, not ours: the
 * frame gets its own storage and cookies and still cannot reach into the
 * embedding page. Dropping `allow-same-origin` does not harden anything — it
 * gives the frame an opaque origin, which breaks every one of these providers
 * outright.
 */
const FRAMED_APP_SANDBOX = ['allow-scripts', 'allow-same-origin'] as const;

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^[0-9]+$/;
const VIMEO_PRIVACY_HASH = /^[A-Za-z0-9]+$/;
const CALENDLY_SEGMENT = /^[A-Za-z0-9_-]+$/;
const GOOGLE_MAPS_PB = /^[A-Za-z0-9!*'().~_-]+$/;

/**
 * Calendly paths that are the scheduling app rather than someone's booking
 * page. Framing one gets a login screen, so they fail closed here instead.
 */
const CALENDLY_RESERVED_SEGMENTS = new Set([
  'app',
  'event_types',
  'login',
  'signup',
  'pages',
  'api',
  'help',
  'blog',
  'pricing',
]);

/**
 * Calendly's own appearance and prefill options, which authors legitimately
 * copy along with the booking URL. Anything else in the query is dropped
 * rather than forwarded — the point of rebuilding the URL is that only things
 * on a list get through.
 */
const CALENDLY_ALLOWED_PARAMS = [
  'hide_gdpr_banner',
  'hide_event_type_details',
  'hide_landing_page_details',
  'primary_color',
  'background_color',
  'text_color',
  'month',
  'date',
] as const;

/** Lowercase and drop a leading `www.`, so host matching is not a trick question. */
export const normaliseHost = (hostname: string): string =>
  hostname.toLowerCase().replace(/^www\./, '');

const pathSegments = (url: URL): string[] =>
  url.pathname.split('/').filter((segment) => segment !== '');

/**
 * Every YouTube URL an author is likely to have in the clipboard — the watch
 * page, the share link, a Short, a live stream, and an embed URL someone
 * copied out of another site's markup — reduced to the video id.
 */
const youTubeVideoId = (url: URL): string | null => {
  const segments = pathSegments(url);
  const host = normaliseHost(url.hostname);

  if (host === 'youtu.be') {
    return segments[0] ?? null;
  }

  if (segments[0] === 'watch') {
    return url.searchParams.get('v');
  }

  if (segments.length === 2 && ['embed', 'shorts', 'live', 'v'].includes(segments[0])) {
    return segments[1];
  }

  return null;
};

export const EMBED_PROVIDERS: readonly EmbedProvider[] = [
  {
    id: 'youtube',
    label: 'YouTube',
    inputHosts: ['youtube.com', 'm.youtube.com', 'youtu.be', 'youtube-nocookie.com'],
    toEmbedUrl: (url) => {
      const videoId = youTubeVideoId(url);
      if (!videoId || !YOUTUBE_ID.test(videoId)) {
        return null;
      }
      // youtube-nocookie.com is the same player without the tracking cookies
      // it would otherwise set on a visitor who never pressed play. Nothing
      // about the embed needs them, and a widget that quietly adds third-party
      // cookies to a customer's site is a consent problem we would be handing
      // them.
      return `https://www.youtube-nocookie.com/embed/${videoId}`;
    },
    sandbox: [...FRAMED_APP_SANDBOX, 'allow-presentation', 'allow-popups', 'allow-popups-to-escape-sandbox'],
    allow: ['accelerometer', 'clipboard-write', 'encrypted-media', 'gyroscope', 'picture-in-picture'],
    allowFullScreen: true,
    defaultAspectRatio: '16:9',
    defaultTitle: 'YouTube video player',
  },
  {
    id: 'vimeo',
    label: 'Vimeo',
    inputHosts: ['vimeo.com', 'player.vimeo.com'],
    toEmbedUrl: (url) => {
      const segments = pathSegments(url);
      // player.vimeo.com/video/123, vimeo.com/123, vimeo.com/channels/staffpicks/123
      const withoutPrefix = segments[0] === 'video' ? segments.slice(1) : segments;
      const numeric = withoutPrefix.filter((segment) => VIMEO_ID.test(segment));
      const videoId = numeric[0];
      if (!videoId) {
        return null;
      }

      // An unlisted video carries a hash, either as a trailing path segment
      // (vimeo.com/123/abc123) or as ?h=. Without it the player refuses to
      // load, so it is the one piece of the input besides the id that is
      // carried across.
      const trailing = withoutPrefix[withoutPrefix.indexOf(videoId) + 1];
      const hash = url.searchParams.get('h') ?? trailing;
      const privacyHash = hash && VIMEO_PRIVACY_HASH.test(hash) ? hash : null;

      return privacyHash
        ? `https://player.vimeo.com/video/${videoId}?h=${privacyHash}`
        : `https://player.vimeo.com/video/${videoId}`;
    },
    sandbox: [...FRAMED_APP_SANDBOX, 'allow-presentation', 'allow-popups', 'allow-popups-to-escape-sandbox'],
    allow: ['autoplay', 'clipboard-write', 'encrypted-media', 'picture-in-picture'],
    allowFullScreen: true,
    defaultAspectRatio: '16:9',
    defaultTitle: 'Vimeo video player',
  },
  {
    id: 'google-maps',
    label: 'Google Maps',
    inputHosts: ['google.com', 'maps.google.com'],
    toEmbedUrl: (url) => {
      // Only the URL from "Share → Embed a map" is accepted, because it is the
      // only one that can be framed at all: Google serves an ordinary maps
      // link with X-Frame-Options and the visitor gets a blank box. Failing
      // here means the author is told to copy the other URL, rather than
      // shipping a page with a hole in it.
      if (pathSegments(url).join('/') !== 'maps/embed') {
        return null;
      }
      const pb = url.searchParams.get('pb');
      // `pb` is Google's own opaque camera-and-place blob. It is carried
      // across verbatim rather than re-encoded, because it is the one input
      // this file cannot regenerate from an id — so instead it is held to a
      // charset with no way out of a query string. Everything that could end
      // one, or start an attribute, is absent from this list.
      if (!pb || !GOOGLE_MAPS_PB.test(pb)) {
        return null;
      }
      return `https://www.google.com/maps/embed?pb=${pb}`;
    },
    sandbox: [...FRAMED_APP_SANDBOX, 'allow-popups', 'allow-popups-to-escape-sandbox'],
    allow: [],
    allowFullScreen: true,
    defaultAspectRatio: '4:3',
    defaultTitle: 'Map',
  },
  {
    id: 'calendly',
    label: 'Calendly',
    inputHosts: ['calendly.com'],
    toEmbedUrl: (url) => {
      const segments = pathSegments(url);
      if (segments.length === 0 || segments.length > 2) {
        return null;
      }
      if (!segments.every((segment) => CALENDLY_SEGMENT.test(segment))) {
        return null;
      }
      if (CALENDLY_RESERVED_SEGMENTS.has(segments[0].toLowerCase())) {
        return null;
      }

      const options = new URLSearchParams();
      for (const parameter of CALENDLY_ALLOWED_PARAMS) {
        const value = url.searchParams.get(parameter);
        if (value !== null) {
          options.set(parameter, value);
        }
      }

      const query = options.toString();
      const path = segments.join('/');
      return query ? `https://calendly.com/${path}?${query}` : `https://calendly.com/${path}`;
    },
    sandbox: [...FRAMED_APP_SANDBOX, 'allow-forms', 'allow-popups', 'allow-popups-to-escape-sandbox'],
    allow: [],
    allowFullScreen: false,
    // A booking widget is a form of unpredictable length, not a picture with a
    // shape. It gets a height instead of a ratio; see `embed-attributes.ts`.
    defaultAspectRatio: 'none',
    defaultTitle: 'Booking calendar',
  },
];

export type ResolvedEmbedUrl = {
  provider: EmbedProvider;
  /** The rebuilt URL. Never the author's string. */
  src: string;
};

/**
 * The single place an embed URL is checked against the allowlist.
 *
 * Every caller — the component, both container resolvers, and the composer's
 * URL editor through the package's public exports — comes through here rather
 * than reading `EMBED_PROVIDERS` and matching for itself. That is what keeps
 * a per-app allowlist a change to one function's argument later on, instead of
 * a hunt for call sites: see
 * `rulecms/__docs__/__TODO__/__COMPONENTS__/TODO_PLATFORM_EMBED_ALLOWLIST_PER_APP.md`.
 *
 * Returns `null` for anything it cannot vouch for, which is the fail-closed
 * behaviour the component depends on. It never throws: a malformed URL in a
 * stored widget must not take down the page it was published to.
 */
export function resolveEmbedUrl(
  value: unknown,
  providers: readonly EmbedProvider[] = EMBED_PROVIDERS
): ResolvedEmbedUrl | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }

  // https only. Every provider here serves it, and requiring it disposes of
  // javascript:, data:, blob: and file: as a class rather than one at a time.
  if (url.protocol !== 'https:') {
    return null;
  }

  // `https://youtube.com@evil.example/` parses with `evil.example` as the host
  // and the rest as credentials. The host check below is not fooled by it, but
  // a URL carrying credentials at all is not one an author pasted from a
  // provider, so it is refused outright.
  if (url.username !== '' || url.password !== '') {
    return null;
  }

  const host = normaliseHost(url.hostname);
  const provider = providers.find((candidate) => candidate.inputHosts.includes(host));
  if (!provider) {
    return null;
  }

  const src = provider.toEmbedUrl(url);
  return src ? { provider, src } : null;
}

/** Whether a URL would render. The composer's editor uses this for its hint. */
export const isAllowedEmbedUrl = (
  value: unknown,
  providers: readonly EmbedProvider[] = EMBED_PROVIDERS
): boolean => resolveEmbedUrl(value, providers) !== null;
